import React from 'react';
import './ValidationErrors.css';

/**
 * Component to display validation errors
 */
const ValidationErrors = ({ errors, fieldName = null }) => {
  if (!errors || Object.keys(errors).length === 0) {
    return null;
  }

  // If fieldName is provided, only show error for that field
  if (fieldName) {
    const error = errors[fieldName];
    if (!error) return null;

    return <div className="validation-error-inline">{error}</div>;
  }

  // Field name mappings for better display
  const fieldNameMap = {
    'employee.employeeNumber': 'Employee Number',
    'employee.employeeFirst': 'Employee First Name',
    'employee.employeeLast': 'Employee Last Name',
    'employee.employeeMiddle': 'Employee Middle Name',
    'employee.email': 'Email Address',
    'employee.phoneNumber': 'Phone Number',
    'store.id': 'Store Location',
    'currentLocation.id': 'Current Location',
    'projectLocation.id': 'Project Location',
    amount: 'Amount',
    reasonMissed: 'Reason for Missed Pay',
    reasonBonus: 'Reason for Bonus',
    reasonMileage: 'Reason for Mileage',
    mileage: 'Mileage',
    hours: 'Hours',
    ptoDate: 'PTO Date',
    reason: 'Reason',
    comments: 'Comments',
    workPerformed: 'Work Performed',
    date: 'Date',
    bonus: 'Bonus Amount',
    'location1.id': 'From Location',
    'location2.id': 'To Location',
    typeOfWork: 'Type of Work',
    isBillable: 'Billable Status',
    supplyOrderSubmitted: 'Supply Order Submitted',
    supplyOrderDate: 'Supply Order Date',
    orderNumber: 'Order Number',
    projectStartDate: 'Project Start Date',
    projectEndDate: 'Project End Date',
    hotelReason: 'Hotel Reason',
    workTicket: 'Work Ticket',
    numOfRooms: 'Number of Rooms',
    numOfPeople: 'Number of People',
    checkIn: 'Check In Date',
    checkOut: 'Check Out Date',
    firstHotelNight: 'First Hotel Night',
    lastHotelNight: 'Last Hotel Night',
    arrivalDate: 'Arrival Date',
    departureDate: 'Departure Date',
    absenceFrom: 'Absence From',
    absenceTo: 'Absence To',
  };

  /**
   * Format array field name for display
   * bonuses[0].date -> Bonus #1: Date
   * mileageDates[2].location1.id -> Mileage Entry #3: From Location
   */
  const formatArrayFieldName = (field) => {
    const arrayMatch = field.match(/^(\w+)\[(\d+)\]\.(.+)$/);
    if (arrayMatch) {
      const [, arrayName, index, fieldName] = arrayMatch;
      const itemNumber = parseInt(index) + 1;

      // Array name mappings
      const arrayNameMap = {
        bonuses: 'Bonus',
        mileageDates: 'Mileage Entry',
        breaks: 'Break',
        targetOrderItems: 'Item',
      };

      const arrayLabel = arrayNameMap[arrayName] || arrayName;
      const fieldLabel =
        fieldNameMap[fieldName] ||
        fieldName
          .split('.')
          .pop()
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim();

      return `${arrayLabel} #${itemNumber}: ${fieldLabel}`;
    }
    return null;
  };

  // Otherwise show all errors as a summary
  const errorList = Object.entries(errors).map(([field, message]) => {
    // Check if it's an array field
    const arrayDisplayField = formatArrayFieldName(field);
    if (arrayDisplayField) {
      return {
        field,
        message,
        displayField: arrayDisplayField,
      };
    }

    // Use custom mapping if available, otherwise format the field name
    const displayField =
      fieldNameMap[field] ||
      field
        .split('.')
        .pop()
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

    return {
      field,
      message,
      displayField,
    };
  });

  return (
    <div className="validation-errors-summary">
      <div className="validation-errors-header">
        <strong>Please correct the following errors:</strong>
      </div>
      <ul className="validation-errors-list">
        {errorList.map(({ field, message, displayField }) => (
          <li key={field} className="validation-error-item">
            <strong>{displayField}:</strong> {message}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ValidationErrors;
